import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    CardElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import BackButton from '../../../Components/BackButton';
import CustomButton from '../../../Components/CustomButton';
import SuccessModal from '../../../Components/SuccessModal/SuccessModal';
import './Payment.css';
import { Formik } from 'formik';
import CustomInput from '../../../Components/CustomInput';
import heartDecor from '../../../assets/images/coin-store-figma/decor-heart.svg';

// Initialize Stripe
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
    import.meta.env.STRIPE_PUBLISHABLE_KEY ||
    'pk_test_51SDjex0ldfFPVZfZt8yiiWyvgB2ixxPTjU18ziB5J26HKQm8nbixSAQcrrQJQ9gapTHCSyyMYwVwpRVoS4rowYXE002MywgNXt';

// Debug log to verify that the key is loaded correctly
console.log('Stripe publishable key in frontend:', stripePublishableKey ? 'Key found' : 'Key missing');

// Only initialize Stripe if we have a valid key
const stripePromise = stripePublishableKey && stripePublishableKey.startsWith('pk_')
    ? loadStripe(stripePublishableKey)
    : null;

const cardElementOptions = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#9e2146',
        },
    },
};

const StripePaymentForm = ({ onBack, amount, onSuccess, onError, clientSecret }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [succeededPaymentIntent, setSucceededPaymentIntent] = useState(null);

    // Initialize Stripe and validate client secret
    useEffect(() => {
        const initializePayment = async () => {
            if (!clientSecret) {
                setIsInitializing(false);
                return;
            }

            // Validate client secret format
            const isValidFormat = clientSecret.startsWith('pi_') && clientSecret.includes('_secret_');

            if (!isValidFormat) {
                console.error('Invalid client secret format! Expected: pi_xxx_secret_xxx');
                setError('Invalid payment configuration. Please try again.');
                setIsInitializing(false);
                return;
            }

            // Simulate initialization time
            setTimeout(() => {
                setIsInitializing(false);
            }, 1000);
        };

        initializePayment();
    }, [clientSecret]);

    const handlePaymentSuccessModalClose = async () => {
        setShowSuccessModal(false);
        if (!succeededPaymentIntent) {
            return;
        }

        try {
            setIsProcessing(true);
            await onSuccess?.(succeededPaymentIntent);
        } catch (callbackError) {
            console.error('Post-payment success callback failed:', callbackError);
            onError && onError(callbackError);
        } finally {
            setIsProcessing(false);
            setSucceededPaymentIntent(null);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            setError('Stripe not loaded yet');
            return;
        }

        if (!clientSecret) {
            setError('Payment intent not initialized. Please try again.');
            return;
        }

        setIsProcessing(true);
        setError(null);

        const cardElement = elements.getElement(CardElement);

        if (!cardElement) {
            setError('Card element not found');
            setIsProcessing(false);
            return;
        }

        try {
            const paymentIntentId = clientSecret.split('_secret_')[0];

            try {
                const retrievedIntent = await stripe.retrievePaymentIntent(clientSecret);

                if (retrievedIntent.error) {
                    console.error('Payment intent retrieval error:', retrievedIntent.error);
                    setError(`Payment intent error: ${retrievedIntent.error.message}`);
                    setIsProcessing(false);
                    return;
                }
            } catch (retrieveError) {
                console.error('Failed to retrieve payment intent:', retrieveError);
                setError('Payment session is invalid. Please try again.');
                setIsProcessing(false);
                return;
            }

            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                },
            });

            if (error) {
                setError(error.message);
                onError && onError(error);
                setIsProcessing(false);
            } else if (paymentIntent.status === 'succeeded') {
                setSucceededPaymentIntent(paymentIntent);
                setShowSuccessModal(true);
                setIsProcessing(false);
            }
        } catch (err) {
            setError('An unexpected error occurred');
            console.log(err);
            onError && onError(err);
            setIsProcessing(false);
        }
    };

    if (!clientSecret || isInitializing) {
        return (
            <div className="payment-page py-sm-5 py-4">
                <Container>
                    <div className="site-card payment-card">
                        <div className="d-flex align-items-center gap-2 mb-4">
                            <BackButton handleBack={onBack} />
                            <h3 className="mb-0 fw-bold">Card Details</h3>
                        </div>
                        <div className="text-center py-4">
                            {!clientSecret ? (
                                <div className="alert alert-warning">
                                    <h5>Payment Initialization Failed</h5>
                                    <p className="mb-3">Unable to initialize payment. Please try again.</p>
                                    <CustomButton
                                        text="Go Back"
                                        onClick={onBack}
                                        className="me-2"
                                    />
                                    <CustomButton
                                        text="Retry"
                                        onClick={() => window.location.reload()}
                                        variant="outline"
                                    />
                                </div>
                            ) : (
                                <>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3">Initializing payment...</p>
                                </>
                            )}
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="payment-page py-sm-5 py-4">
            <Container>
                <div className="site-card payment-card">
                    <div className="d-flex align-items-center gap-2 mb-4">
                        <BackButton handleBack={onBack} />
                        <h3 className="mb-0 fw-bold">Card Details</h3>
                    </div>

                    <div className="amount-display text-center mb-4">
                        <p className="mb-1">Amount:</p>
                        <h4 className="fw-bold">${amount?.toFixed(2)}</h4>
                    </div>
                    <Formik
                        initialValues={{ name: '' }}
                        // validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, errors, touched, handleChange, handleBlur }) => (
                            <form onSubmit={handleSubmit} className="stripe-form">
                                <div className="mb-3">
                                    <CustomInput
                                        label="Card Holder Name"
                                        placeholder="Enter Card Holder Name"
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="form-label">Card Details</label>
                                    <div className="stripe-card-element">
                                        <CardElement options={cardElementOptions} />
                                    </div>
                                    {error && (
                                        <div className="text-danger mt-2">
                                            {error}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4">
                                    <CustomButton
                                        text={isProcessing ? "Processing..." : "Pay Now"}
                                        type="submit"
                                        className="w-100"
                                        disabled={!stripe || isProcessing}
                                        loading={isProcessing}
                                    />
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            </Container>
            <SuccessModal
                show={showSuccessModal}
                onClose={handlePaymentSuccessModalClose}
                title="Successful"
                description="Payment successfully made!"
            />
        </div>
    );
};

const StripePayment = ({ onBack, amount, onSuccess, onError, clientSecret }) => {
    if (!stripePromise) {
        return (
            <section className="payment-figma-page">
                <img src={heartDecor} alt="" className="payment-figma-heart" aria-hidden="true" />
                <div className="payment-figma-hero">
                    <Container>
                        <h1 className="payment-figma-title">Payment Details</h1>
                    </Container>
                </div>
                <div className="payment-figma-main payment-page py-sm-5 py-4">
                    <Container>
                        <div className="site-card payment-card">
                            <div className="alert alert-danger">
                                <h5>Stripe Configuration Error</h5>
                                <p className="mb-3">Stripe publishable key is not configured. Please check your environment variables.</p>
                                <CustomButton
                                    text="Go Back"
                                    onClick={onBack}
                                />
                            </div>
                        </div>
                    </Container>
                </div>
            </section>
        );
    }

    return (
        <section className="payment-figma-page">
            <img src={heartDecor} alt="" className="payment-figma-heart" aria-hidden="true" />
            <div className="payment-figma-hero">
                <Container>
                    <h1 className="payment-figma-title">Payment Details</h1>
                </Container>
            </div>
            <div className="payment-figma-main">
            <Elements stripe={stripePromise}>
                <StripePaymentForm
                    onBack={onBack}
                    amount={amount}
                    onSuccess={onSuccess}
                    onError={onError}
                    clientSecret={clientSecret}
                />
            </Elements>
            </div>
        </section>
    );
};

export default StripePayment;
